import { useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { useEffect } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useLogout, useUsername } from "@/auth/AuthProvider";
import { useFeatures, useTabs } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { useApi } from "@/lib/api";
import { ANALYTICS_STALE_MS, analyticsPrefetchSpecs } from "@/lib/searchUsage";
import type { AccountOwner } from "@/lib/types";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  // Tab key matching ``/api/me`` ``tabs`` — access + nav visibility are resolved
  // server-side from the admin config, so the nav just reads ``tabs[key].listed``.
  // (The label here is the fallback; the resolved label from the server wins.)
  key: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/search", key: "search", label: "Search" },
  { to: "/service-providers", key: "service_providers", label: "Service providers" },
  { to: "/introduction-analyzer", key: "introduction_analyzer", label: "Introduction analyzer" },
  { to: "/portco-analysis", key: "portco_analysis", label: "Portco needs" },
  { to: "/transcript-processor", key: "transcript_processor", label: "Transcript processor" },
  { to: "/analytics", key: "analytics", label: "Analytics" },
  { to: "/scout-analytics", key: "scout_analytics", label: "Web-sourced" },
  { to: "/exclusions", key: "exclusions", label: "Exclusions" },
  { to: "/admin", key: "admin", label: "Admin" },
];

/**
 * Top-level chrome for the inspector. Logo placeholder follows the brand's
 * "BluWave" wordmark spec (Hanken Grotesk 700, navy) since we don't have a
 * raster logo committed to the repo. Active nav item underlines in brand
 * blue; inactive nav items stay in muted ink.
 */
export function Layout() {
  const username = useUsername();
  const logout = useLogout();
  // Drives the nav filter. While the ``/api/me`` query is in flight ``tabs`` is
  // ``{}`` so gated links don't flash before the answer arrives — they appear
  // progressively when the backend confirms ``listed`` for each tab.
  const tabs = useTabs();
  const features = useFeatures();
  const visibleNav = NAV_ITEMS.map((item) => ({
    ...item,
    // Prefer the server-resolved label so a rename in the catalog shows up here.
    label: tabs[item.key]?.label ?? item.label,
  })).filter((item) => tabs[item.key]?.listed);

  // Warm the account-owner list at app startup (once the user is known to have
  // the feature) so the browse-by-owner dropdown is ready the moment they open
  // the portco tab. It's a slightly heavy aggregation whose result rarely
  // changes — staleTime Infinity means the page's own query reads this cache
  // rather than re-firing. Gated on the feature so non-portco users never call
  // the (allowlisted) endpoint.
  const queryClient = useQueryClient();
  const apiCall = useApi();
  const hasPortco = features.includes("portco_analysis");
  useEffect(() => {
    if (!hasPortco) return;
    void queryClient.prefetchQuery({
      queryKey: ["portco-account-owners"],
      queryFn: ({ signal }) =>
        apiCall<AccountOwner[]>("/api/portco-analysis/account-owners", { signal }),
      staleTime: Infinity,
      gcTime: Infinity,
    });
  }, [hasPortco, queryClient, apiCall]);

  // Warm the Analytics tab at app startup for anyone who can open it (the tab
  // is admin-gated, so this fires for admins only — non-admins never call the
  // gated endpoints). The specs cover the tab's default first paint, headlined
  // by the search-usage DATASET: one fetch of every log row in the window,
  // which the panel then slices entirely in memory — so by the time an admin
  // opens the tab, every slicer is instant and no interaction re-hits
  // Snowflake. prefetchQuery swallows errors (a failed warm just means the
  // page fetches on mount as before).
  const hasAnalytics = tabs.analytics?.accessible === true;
  useEffect(() => {
    if (!hasAnalytics) return;
    for (const spec of analyticsPrefetchSpecs()) {
      void queryClient.prefetchQuery({
        queryKey: spec.queryKey,
        queryFn: ({ signal }) => apiCall<unknown>(spec.path, { query: spec.query, signal }),
        staleTime: ANALYTICS_STALE_MS,
      });
    }
  }, [hasAnalytics, queryClient, apiCall]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-bw-blue-muted/40">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center gap-8">
          <Link
            to="/search"
            className="flex items-baseline gap-2 text-bw-navy"
            aria-label="BluWave qualification inspector home"
          >
            <span className="font-sans text-lg font-bold tracking-tight">
              BluWave
            </span>
            <span className="font-sans text-sm font-normal text-bw-ink-muted">
              Qualification inspector
            </span>
          </Link>
          {/* Active item gets a brand-blue 2px underline; inactive items
              keep a transparent border of the same width so the row's
              vertical layout never shifts when navigation changes. */}
          <nav className="flex gap-6 font-sans text-sm">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "py-1 border-b-2 border-transparent text-bw-ink-muted transition-colors hover:text-bw-navy",
                    isActive && "text-bw-navy font-semibold border-bw-blue",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            {username && (
              <span className="font-sans text-sm text-bw-ink-muted">{username}</span>
            )}
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
